import React, {Fragment} from "react";
import {format} from "date-fns";
import {Meta} from "@directus/sdk";
import {useLoaderData, useOutletContext} from "@remix-run/react";
import {json} from "@remix-run/node";
import type {MetaFunction, LoaderFunction, LinksFunction} from "@remix-run/node";
import {Banner, Breadcrumbs, Pagination, PostCard, OffersSlider} from "@bettorsignals/ui";
import {Facebook, Instagram, Telegram, Twitter} from "@bettorsignals/assets";
import {Collection, isPost, Status} from "@bettorsignals/api";
import type {Post} from "@bettorsignals/api";
import {$api} from "~/api";
import {LinkTo} from "~/utils/LinkTo";
import type {WithOffersOutletContext} from "~/routes/__with-offers";
import {useLinkTo} from "~/hooks/useLinkTo";

interface LoaderData {
    posts: Post[];
    pagesCount: number;
    currentPage: number;
}

export const links: LinksFunction = () => {
    return [{rel: "canonical", href: `${LinkTo.fullPageUrl()}${LinkTo.blogPage()}`}];
};

export const loader: LoaderFunction = async ({request}) => {
    const POSTS_PER_PAGE = 8;

    const pageUrlParameter = new URL(request.url).searchParams.get("page");
    const currentPage = (pageUrlParameter && Number.parseInt(pageUrlParameter)) || 1;

    const postsResponse = await $api.items(Collection.Posts).readByQuery({
        filter: {
            status: {
                _eq: Status.Published
            }
        },
        sort: ["-datePublished"],
        meta: Meta.FILTER_COUNT,
        limit: POSTS_PER_PAGE,
        page: currentPage
    });

    const pagesCount = Math.ceil(postsResponse.meta?.filter_count! / POSTS_PER_PAGE);

    return json<LoaderData>({
        posts: postsResponse.data?.filter(isPost) ?? [],
        currentPage,
        pagesCount
    });
};

export const meta: MetaFunction = () => {
    return {
        title: "Blog | BettorSignals.com",
        description: "Browse expert articles on all sports & betting-related topics."
    };
};

export default function Blog() {
    const {posts, pagesCount, currentPage} = useLoaderData<LoaderData>();
    const {offers} = useOutletContext<WithOffersOutletContext>();
    const {linkTo} = useLinkTo();

    return (
        <>
            <div className="flex flex-col gap-8">
                <Breadcrumbs
                    pages={[
                        {
                            name: "Home",
                            href: LinkTo.homePage()
                        },
                        {
                            name: "Blog",
                            href: LinkTo.blogPage()
                        }
                    ]}
                />

                <div className="py-12 md:py-16">
                    <Banner
                        contents={[
                            {
                                text: "Want more free tips?"
                            },
                            {
                                text: "Grab them on our social media.",
                                className: "text-bs-gradient-primary"
                            }
                        ]}
                        socialMediaIcons={[
                            {
                                Icon: Instagram,
                                url: LinkTo.instagramPage()
                            },
                            {
                                Icon: Facebook,
                                url: LinkTo.facebookGroup()
                            },
                            {
                                Icon: Twitter,
                                url: LinkTo.twitterPage()
                            },
                            {
                                Icon: Telegram,
                                url: LinkTo.telegramChannel()
                            }
                        ]}
                    />
                </div>
            </div>

            <h1 className="mt-14 text-2xl font-bold leading-7 text-gray-900">See all articles</h1>
            <div className="my-[42px] flex flex-col gap-[62px]">
                {posts.map((post, index, thisArray) => {
                    const OFFERS_SLIDER_RENDER_INDEX = Math.min(thisArray.length - 1, 2);

                    const shouldRenderOffersSlider =
                        offers.length > 0 && index === OFFERS_SLIDER_RENDER_INDEX;

                    return (
                        <Fragment key={post.id}>
                            <PostCard
                                date={format(new Date(post.datePublished), "MMM d, yyyy")}
                                title={post.title}
                                coverImageUrl={linkTo.asset(post.cover, {
                                    height: 152,
                                    quality: 100,
                                    seoFriendlyName: `${post.title} cover image`
                                })}
                                description={post.contentPreview}
                                linkCta="Read full story"
                                href={post.slug}
                            />
                            {shouldRenderOffersSlider && (
                                <div className="block md:hidden">
                                    <OffersSlider offers={offers} />
                                </div>
                            )}
                        </Fragment>
                    );
                })}
            </div>

            <div className="mb-12 md:mb-24">
                <Pagination pagesToDisplay={5} pagesCount={pagesCount} currentPage={currentPage} />
            </div>
        </>
    );
}
